<script setup>
import { Head } from "@inertiajs/vue3";
import { computed, inject } from "vue";
import { useAssetTransforms } from "@/Composables";
import _ from "lodash";

const props = defineProps({
    meta: {
        type: Array,
        required: false,
        default: [],
    },
    title: {
        type: String,
        required: false,
    },
    titlePrefix: {
        type: String,
        required: false,
        default: "",
    },
    description: {
        type: String,
        required: false,
    },
    siteNameBeforeTitle: {
        type: Boolean,
        required: false,
        default: false,
    },
    ogTitle: {
      type: String,
      required: false,
      default: null,
    },
    ogType: {
        type: String,
        required: false,
        default: null,
    },
    ogImagePadTransform: {
        type: Boolean,
        required: false,
        default: false,
    },
    ogImageSource: {
        type: String,
        default: 'marketing',
        required: false
    },
    ogImage: {
        type: String,
        required: false,
    },
    ogImageWidth: {
        type: Number,
        required: false,
        default: 1200,
    },
    ogImageHeight: {
        type: Number,
        required: false,
        default: 630,
    },
    ogImageType: {
        type: String,
        required: false,
        default: "image/png",
    },
    url: {
        type: String,
        required: false,
    },
    robots: {
        type: String,
        required: false,
        default: "all",
    },
    jsonLdMainEntity: {
        type: Object,
        required: false,
    },
    jsonLdBreadcrumbs: {
        type: Array,
        required: false,
        default: [],
    },
});

const route = inject("route");
const { marketingAssetUrl, assetUrl } = useAssetTransforms();

const siteName = "SECLOCK";
const divider = "|";
const defaultTitle = "B2B Electrical & Mechanical Door Hardware";
const defaultDescription =
    "The master distributor of electrical and mechanical door hardware for professionals. With over 100 lines in stock, decades of experience, and innovative digital tools, SECLOCK can get your business what you need - shipped same-day.";
const defaultOgImage = "seclock_og_default";
const defaultOgType = "website";
const ogImageTransform = "meta-og-image";
const ogImageTransformPad = "meta-og-image-pad";

const getMeta = (type) => {
    if (props.meta.length) {
        let meta = _.find(props.meta, { meta_type: type });
        return meta?.value || null;
    } else {
        return null;
    }
};

const prepareTitle = (title) => {
    return props.siteNameBeforeTitle
        ? `${siteName} ${divider} ${props.titlePrefix}${title}`
        : `${props.titlePrefix}${title} ${divider} ${siteName}`;
};

const titleFallback = computed(() => {
    return props.title ? props.title : defaultTitle;
});

const descriptionFallback = computed(() => {
    return props.description ? props.description : defaultDescription;
});

const ogTypeFallback = computed(() => {
    return props.ogType ? props.ogType : defaultOgType;
});

const ogImageFallback = computed(() => {
    return props.ogImage ? props.ogImage : defaultOgImage;
});

const metaTitle = computed(() => {
    let title = getMeta("title");
    return title ? prepareTitle(title) : prepareTitle(titleFallback.value);
});

const metaOgTitle = computed(() => {
    let title = getMeta("og:title") ? getMeta("og:title") : props.ogTitle;
    return title ? prepareTitle(title) : metaTitle.value;
});

const metaTwitterTitle = computed(() => {
    let title = getMeta("twitter:title") ? getMeta("twitter:title") : props.ogTitle;
    return title ? prepareTitle(title) : metaTitle.value;
});

const metaDescription = computed(() => {
    let description = getMeta("description");
    return description ? description : descriptionFallback.value;
});

const metaOgDescription = computed(() => {
    let description = getMeta("og:description");
    return description ? description : metaDescription.value;
});

const metaTwitterDescription = computed(() => {
    let description = getMeta("twitter:description");
    return description ? description : metaDescription.value;
});

const metaRobots = computed(() => {
    return getMeta("robots") ?? props.robots;
});

const metaOgType = computed(() => {
    let type = getMeta("og:type");
    return type ? type : ogTypeFallback.value;
});

const metaOgImageType = computed(() => {
    let type = getMeta("og:image:type");
    return type ? type : props.ogImageType;
});

const imageExtension = computed(() => {
    return metaOgImageType.value.replace("image/", ".");
});

const metaOgImageTransform = computed(() => {
    return props.ogImagePadTransform
        ? ogImageTransformPad
        : ogImageTransform;
});

const getAssetUrl = (asset, transform) => {
    return (props.ogImageSource === 'marketing') ? marketingAssetUrl(asset, transform) : assetUrl(asset, transform);
};

const metaOgImage = computed(() => {
    let image = getMeta("og:image");
    image = image ? image : ogImageFallback.value;
    return getAssetUrl(
        `${image}${imageExtension.value}`,
        metaOgImageTransform.value
    );
});

const metaTwitterImage = computed(() => {
    let image = getMeta("twitter:image");
    return image
        ? getAssetUrl(`${image}${imageExtension.value}`, metaOgImageTransform.value)
        : metaOgImage.value;
});

const metaTwitterCard = computed(() => {
    let card = getMeta("twitter:card");
    return card ? card : "summary_large_image";
});

const metaOgImageWidth = computed(() => {
    let width = getMeta("og:image:width");
    return width ? width : props.ogImageWidth;
});

const metaOgImageHeight = computed(() => {
    let height = getMeta("og:image:height");
    return height ? height : props.ogImageHeight;
});

const metaUrl = computed(() => {
    return props.url ? props.url : route(route().current(), route().params);
});

const jsonLdBreadcrumbs = computed(() => {
    if (!props.jsonLdBreadcrumbs.length) {
        return null;
    }

    let crumbs = [];

    props.jsonLdBreadcrumbs.forEach((crumb, index) => {
        crumbs.push({
            "@type": "ListItem",
            position: index + 1,
            name: crumb.title,
            item: crumb.url,
        });
    });

    return crumbs;
});

const jsonLdData = computed(() => {
    // Organization schema
    const organizationSchema = {
        "@type": "Organization",
        "@id": "https://www.seclock.com/#organization",
        name: "SECLOCK",
        legalName: "SECLOCK Distribution, Inc.",
        url: route("web.home"),
        description: "The distributor of commercial electrical and mechanical door hardware.",
        additionalProperty: {
            "@type": "PropertyValue",
            "name": "Product Ontology",
            "value": "http://www.productontology.org/id/Electronic_lock"
        },
        logo: {
            "@type": "ImageObject",
            "@id": "https://www.seclock.com/#logo",
            url: marketingAssetUrl(
                `${defaultOgImage}${imageExtension.value}`,
                metaOgImageTransform.value
            ),
            caption: "SECLOCK logo",
            width: 1200,
            height: 630
        },
        email: "info@seclock.com",
        telephone: "+1 800-847-5625",
        contactPoint: [
            {
                "@type": "ContactPoint",
                telephone: "+1-800-847-5625",
                contactType: "customer service",
                areaServed: "US",
                availableLanguage: "English"
            },
            {
                "@type": "ContactPoint",
                faxNumber: "+1-800-878-6400",
                contactType: "customer service",
                areaServed: "US"
            },
            {
                "@type": "ContactPoint",
                telephone: "+1-833-344-1103",
                contactType: "customer service",
                areaServed: "US",
                availableLanguage: "English",
                description: "Text messaging only"
            },
            {
                "@type": "ContactPoint",
                email: "info@seclock.com",
                contactType: "customer service",
                areaServed: "US"
            },
            {
                "@type": "ContactPoint",
                email: "orders@seclock.com",
                contactType: "sales",
                areaServed: "US"
            },
            {
                "@type": "ContactPoint",
                email: "quotes@seclock.com",
                contactType: "sales",
                areaServed: "US"
            },
            {
                "@type": "ContactPoint",
                email: "credit@seclock.com",
                contactType: "billing support",
                areaServed: "US"
            },
            {
                "@type": "ContactPoint",
                email: "onsite@seclock.com",
                contactType: "technical support",
                areaServed: "US",
                description: "On-site video chat assistance"
            }
        ],
        address: {
            "@type": "PostalAddress",
            streetAddress: "P.O. Box 95",
            addressLocality: "Westwood",
            addressRegion: "MA",
            postalCode: "02090",
            addressCountry: "US",
        },
        sameAs: [
            "https://www.facebook.com/seclockofficial/",
            "https://www.linkedin.com/company/seclock/",
            "https://www.instagram.com/seclockofficial/?hl=en",
            "https://twitter.com/seclock",
            "https://www.youtube.com/channel/UCja3JyZ9S8bX6QteouDFwCg",
        ],
    };

    // Build the @graph array with Organization as the first item
    const graphItems = [organizationSchema];

    // Add WebSite schema as a separate top-level entity for Home page only
    if (route().current() === 'web.home') {
        graphItems.push({
            "@type": "WebSite",
            "@id": "https://www.seclock.com/#website",
            name: "SECLOCK",
            url: route("web.home"),
            publisher: {
                "@id": "https://www.seclock.com/#organization"
            },
            potentialAction: {
                "@type": "SearchAction",
                target: {
                    "@type": "EntryPoint",
                    urlTemplate: "https://www.seclock.com/catalog/products?q={search_term_string}"
                },
                "query-input": "required name=search_term_string"
            }
        });
    }

    // Always create a WebPage schema
    const webPageSchema = {
        "@type": "WebPage",
        "@id": route(route().current(), route().params),
        name: metaTitle.value,
        description: metaDescription.value,
        url: route(route().current(), route().params),
        isPartOf: {
            "@id": "https://www.seclock.com/#website"
        },
        about: {
            "@id": "https://www.seclock.com/#organization"
        },
        audience: {
            "@type": "BusinessAudience",
            audienceType: "Businesses",
            description:
                "This website caters to commercial door hardware, security, and access control professionals and organizations, spanning across multiple industries such as locksmith, contract hardware, glass and glazing, systems integrator, OEM, MRO, and online retail. Our platform features an extensive range of products from well-known brands like Schlage, Von Duprin, LCN, HES, Adams Rite, Corbin Russwin, Sargent, Yale, Norton, and Dormakaba.",
        },
    };

    // Add breadcrumbs to WebPage if available
    if (jsonLdBreadcrumbs.value) {
        webPageSchema.breadcrumb = {
            "@type": "BreadcrumbList",
            itemListElement: jsonLdBreadcrumbs.value,
        };
    }

    // If a custom mainEntity is provided (CollectionPage, Article, etc.), nest it inside WebPage
    if (props.jsonLdMainEntity) {
        webPageSchema.mainEntity = props.jsonLdMainEntity;
    }

    graphItems.push(webPageSchema);

    return {
        "@context": "https://schema.org",
        "@graph": graphItems
    };
});
</script>

<template>
    <Head>
        <title>{{ metaTitle }}</title>
        <meta name="description" :content="metaDescription" />
        <meta name="og:type" :content="metaOgType" />
        <meta property="og:title" :content="metaOgTitle" />
        <meta property="og:description" :content="metaOgDescription" />
        <meta property="twitter:title" :content="metaTwitterTitle" />
        <meta
            property="twitter:description"
            :content="metaTwitterDescription"
        />
        <meta property="og:image" :content="metaOgImage" />
        <meta property="og:image:type" :content="metaOgImageType" />
        <meta property="og:image:width" :content="metaOgImageWidth" />
        <meta property="og:image:height" :content="metaOgImageHeight" />
        <meta property="twitter:image" :content="metaTwitterImage" />
        <meta name="twitter:card" :content="metaTwitterCard" />
        <meta property="og:url" :content="metaUrl" />
        <link rel="canonical" :href="metaUrl" />
        <link rel="alternate" type="text/markdown" title="LLM-friendly content" href="https://www.seclock.com/llms.txt" />
        <meta name="robots" :content="metaRobots" />
    </Head>
    <component is="script" type="application/ld+json">
        {{ JSON.stringify(jsonLdData) }}
    </component>
</template>
